import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, ChevronLeft, ChevronRight, Navigation2 } from 'lucide-react';
import { VenueApiResult } from '../../../types/venue';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';

interface VenueGalleryProps {
  venues: VenueApiResult[];
  selectedVenueId: string | null;
  onSelectVenue: (venueId: string) => void;
  onClose: () => void;
}

const VenueGallery: React.FC<VenueGalleryProps> = ({
  venues,
  selectedVenueId,
  onSelectVenue,
  onClose
}) => {
  const navigate = useNavigate();
  const galleryRef = React.useRef<HTMLDivElement>(null);

  const selectedVenue = venues.find(venue => venue.id === selectedVenueId);
  const selectedIndex = venues.findIndex(venue => venue.id === selectedVenueId);

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      onSelectVenue(venues[selectedIndex - 1].id);
    }
  };

  const handleNext = () => {
    if (selectedIndex < venues.length - 1) {
      onSelectVenue(venues[selectedIndex + 1].id);
    }
  };

  const handleClubClick = async (venue: VenueApiResult) => {
    try {
      const { data: clubData, error } = await supabase
        .from('clubs')
        .select('club_id')
        .ilike('club_name', `%${venue.name}%`)
        .maybeSingle();

      if (error) {
        console.error('Supabase error:', error.message);
        return;
      }

      if (!clubData?.club_id) {
        console.warn(`No matching club found for: "${venue.name}"`);
        return;
      }

      navigate(`/dashboard/clubs/${clubData.club_id}`);
    } catch (err) {
      console.error('Unexpected error navigating to club profile:', err);
    }
  };

  React.useEffect(() => {
    if (galleryRef.current && selectedVenueId) {
      const selectedElement = galleryRef.current.querySelector(`[data-venue-id="${selectedVenueId}"]`);
      if (selectedElement) {
        const containerWidth = galleryRef.current.offsetWidth;
        const elementWidth = selectedElement.clientWidth;
        const elementLeft = (selectedElement as HTMLElement).offsetLeft;

        galleryRef.current.scrollTo({
          left: elementLeft - (containerWidth / 2) + (elementWidth / 2),
          behavior: 'smooth'
        });
      }
    }
  }, [selectedVenueId]);

  if (!selectedVenue) return null;

  return (
    <div className="relative w-full h-full">
      {/* Arrows outside gallery */}
      {selectedIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
          }}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-dark-200 hover:bg-dark-300 text-white p-2 rounded-full shadow-lg"
          aria-label="Previous venue"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}

      {selectedIndex < venues.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-dark-200 hover:bg-dark-300 text-white p-2 rounded-full shadow-lg"
          aria-label="Next venue"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}

      {/* Close Button */}
      <div className="absolute right-2 top-2 z-20">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="p-2 rounded-full bg-dark-200 text-white hover:bg-dark-300 transition-colors shadow-lg"
          aria-label="Close gallery"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Padded container for gallery row */}
      <div className="p-4">
    <div
  ref={galleryRef}
  id="no-swipe-zone"
  className="flex space-x-6 overflow-x-auto pb-2 scrollbar-none snap-x snap-mandatory scroll-px-4"
  onTouchStart={(e) => e.stopPropagation()}
  onTouchMove={(e) => e.stopPropagation()}
>

          {venues.map((venue) => (
            <div
              key={venue.id}
              data-venue-id={venue.id}
              className={cn(
                "flex-shrink-0 snap-center",
                "cursor-pointer transition-all duration-200",
                selectedVenueId === venue.id ? "opacity-100 scale-100" : "opacity-70 scale-95 hover:opacity-90"
              )}
              onClick={() => onSelectVenue(venue.id)}
            >
              <div className={cn(
                "bg-dark-200 rounded-lg overflow-hidden border",
                "shadow-lg transform transition-all duration-300",
                selectedVenueId === venue.id ? "border-gold-400" : "border-dark-300",
                "w-64 sm:w-72"
              )}>
                {venue.photos && venue.photos.length > 0 ? (
                  <div className="h-32 w-full">
                    <img
                      src={venue.photos[0].url}
                      alt={venue.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.currentTarget;
                        const parent = target.parentElement;
                        if (parent) {
                          // Create MapPin container
                          const container = document.createElement('div');
                          container.className = "w-full h-full flex items-center justify-center bg-dark-300";
                          
                          // Create MapPin icon
                          const mapPinIcon = document.createElement('div');
                          mapPinIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-dark-800"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`;
                          
                          container.appendChild(mapPinIcon);
                          parent.replaceChild(container, target);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-32 w-full bg-dark-300 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-dark-800" />
                  </div>
                )}
                <div className="p-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClubClick(venue);
                    }}
                    className="text-lg font-semibold text-white hover:text-gold-400 transition-colors text-left block w-full truncate"
                  >
                    {venue.name}
                  </button>

                  <div className="flex items-center text-dark-800 text-sm mt-1">
                    <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="truncate">{venue.address}</span>
                  </div>

                  {venue.rating > 0 && (
                    <div className="flex items-center mt-2">
                      <Star className="h-4 w-4 text-gold-400 mr-1" />
                      <span className="text-white font-medium">{venue.rating.toFixed(1)}</span>
                      {venue.user_ratings_total && (
                        <span className="text-dark-800 text-sm ml-1">
                          ({venue.user_ratings_total})
                        </span>
                      )}
                    </div>
                  )}

                  {venue.activities && venue.activities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {venue.activities.slice(0, 3).map((activity, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 bg-dark-300 rounded-full text-white"
                        >
                          {activity}
                        </span>
                      ))}
                      {venue.activities.length > 3 && (
                        <span className="text-xs px-2 py-0.5 bg-dark-300 rounded-full text-white">
                          +{venue.activities.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {venue.coordinates && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(venue.address)}`, '_blank');
                      }}
                      className="mt-3 w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gold-400 hover:bg-gold-500 text-dark-50 rounded-lg transition-colors"
                    >
                      <Navigation2 className="h-4 w-4" />
                      <span>Navigate</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VenueGallery;